import React from 'react';
import { Heart, MapPin, Briefcase } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import { trackUserInteraction } from '@/utils/performanceMonitoring';
import { getFlagEmoji } from '@/utils/countryFlags';

interface CardImageProps {
  imageSrc: string;
  name: string;
  displayDiscipline: string;
  location: string;
  isLiked: boolean;
  isHovered: boolean;
  isFeatured: boolean;
  isLiking?: boolean;
  onLike: (e: React.MouseEvent) => void;
  isMobile?: boolean;
  hire_me_enabled?: boolean;
}

// Helper function to get profession icon (simplified version)
const getProfessionIcon = (discipline: string) => {
  // Default to briefcase icon
  return <Briefcase className="h-3.5 w-3.5 mr-1 shrink-0" />;
};

export const CardImage = ({ 
  imageSrc, 
  name, 
  displayDiscipline, 
  location, 
  isLiked, 
  isHovered, 
  isFeatured,
  isLiking,
  onLike,
  isMobile = false,
  hire_me_enabled = false
}: CardImageProps) => {
  // Use a reduced aspect ratio on mobile (0.7 or 70% of the original height)
  const aspectRatio = isMobile ? 1/0.7 : 1/1;
  
  const handleLikeClick = (e: React.MouseEvent) => {
    // Track the like interaction using the performance monitoring utility
    trackUserInteraction('card-like-click', () => {
      // Call the existing onLike handler
      onLike(e);
    });
  };
  
  return (
    <div className="relative">
      {isMobile ? (
        <div className="card-image-container">
          <img 
            src={imageSrc || '/placeholder.svg'} 
            alt={`${name} - ${displayDiscipline} in ${location}`}
            loading="lazy"
          />
        </div>
      ) : (
        <AspectRatio ratio={aspectRatio}>
          <img 
            src={imageSrc || '/placeholder.svg'} 
            alt={`${name} - ${displayDiscipline} in ${location}`}
            className="w-full h-full object-cover object-center"
            loading="lazy"
          />
        </AspectRatio>
      )}
      
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent"></div>
      
      {hire_me_enabled && isFeatured && (
        <div className={cn(
          "absolute top-2 flex items-center group",
          isHovered ? "right-2" : "right-4"
        )}>
          {/* Default state: Blinking dot visible only on desktop, hidden on hover */}
          {!isMobile && (
            <div className={cn(
              "status-dot-container shrink-0",
              !isMobile && "group-hover:hidden"
            )}>
              <span className="ping-ring"></span>
              <span className="inner-dot"></span>
            </div>
          )}

          {/* Badge: Visible on mobile, slides in on hover for desktop */}
          <div className={cn(
            "relative overflow-hidden bg-white rounded-full shadow-sm py-1 flex gap-2 items-center whitespace-nowrap",
            !isMobile && "transition-all duration-330 ease-out", // Transitions only for desktop
            isMobile ? "max-w-full opacity-100 px-3" : "max-w-0 group-hover:max-w-xs opacity-0 group-hover:opacity-100 px-0 group-hover:px-3"
          )}>
            <span className={cn(
              "text-xs font-medium text-gray-900",
              !isMobile && "transition-opacity duration-200 pointer-events-none", // Transitions/pointer-events only for desktop
              isMobile ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            )}>
              Available for work
            </span>
            {/* Blinking dot inside the badge: always visible within badge */}
            <span className="status-dot-container shrink-0">
              <span className="ping-ring"></span>
              <span className="inner-dot"></span>
            </span>
          </div>
        </div>
      )}
      
      <div className="absolute bottom-0 left-0 p-3 text-white w-full">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center text-sm">
            {getProfessionIcon(displayDiscipline)}
            <p className="truncate drop-shadow-md font-medium">{displayDiscipline}</p>
          </div>
          {location && (
            <div className="flex items-center text-sm ml-2">
              <span className="text-base leading-none mr-1">{getFlagEmoji(location)}</span>
              <p className="truncate drop-shadow-md font-medium">{location}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
